import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Send, CheckCircle, AlertCircle, RefreshCw } from "lucide-react";
import { sendContactFormEmail } from "@/functions/sendContactFormEmail";

export default function QuickContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [captcha, setCaptcha] = useState({ a: 0, b: 0, answer: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errors, setErrors] = useState({});

  const generateCaptcha = () => {
    setCaptcha({
      a: Math.floor(Math.random() * 10) + 1,
      b: Math.floor(Math.random() * 10) + 1,
      answer: "",
    });
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!form.email.trim()) errs.email = "Required";
    if (!form.message.trim()) errs.message = "Required";
    if (parseInt(captcha.answer) !== captcha.a + captcha.b) errs.captcha = "Incorrect answer";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStatus("sending");
    try {
      const res = await sendContactFormEmail(form);
      if (res.data?.success) {
        setStatus("success");
        setForm({ name: "", email: "", phone: "", message: "" });
        generateCaptcha();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-6 flex flex-col items-center text-center gap-3">
          <CheckCircle className="w-12 h-12 text-green-600" />
          <h3 className="text-xl font-bold text-foreground">Message Sent!</h3>
          <p className="text-muted-foreground text-sm">
            Thanks for reaching out. We'll get back to you shortly.
          </p>
          <Button variant="outline" onClick={() => setStatus("idle")}>
            Send Another Message
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-xl border-border/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="w-5 h-5 text-primary" />
          Quick Contact Form
        </CardTitle>
      </CardHeader>
      <CardContent>
        {status === "error" && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            Something went wrong. Please try again or call us at 832-970-4583.
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Name {errors.name && <span className="text-red-500">*</span>}</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                className={errors.name ? "border-red-400" : ""}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Phone</label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Your phone (optional)"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Email {errors.email && <span className="text-red-500">*</span>}</label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
              className={errors.email ? "border-red-400" : ""}
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Message {errors.message && <span className="text-red-500">*</span>}</label>
            <Textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="How can we help you?"
              rows={4}
              className={errors.message ? "border-red-400" : ""}
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">
              Spam Check: What is {captcha.a} + {captcha.b}? {errors.captcha && <span className="text-red-500">*</span>}
            </label>
            <div className="flex gap-2">
              <Input
                type="number"
                value={captcha.answer}
                onChange={(e) => setCaptcha({ ...captcha, answer: e.target.value })}
                placeholder="Your answer"
                className={`max-w-xs ${errors.captcha ? "border-red-400" : ""}`}
              />
              <Button type="button" variant="outline" size="icon" onClick={generateCaptcha} title="New question">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={status === "sending"}
          >
            {status === "sending" ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sending...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send className="w-4 h-4" />
                Send Message
              </span>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}